/**
 * Left-nav keys ordered by how often the user reaches for them.
 * The rendering layer in LeftPanelComponent must follow this order.
 * Networking was removed (was unused / never planned).
 */
export const MENU = {
    /** AI assistant home (landing + chat with history). */
    HOME: 'home',
    CALENDAR: 'calendar',
    APPOINTMENTS: 'appointments',
    WAITLIST: 'waitlist',
    CLIENT: 'client',
    PAYMENTS: 'payments',
    REPORTS: 'reports',
    CHATS: 'chats',
    SERVICES: 'services',
    TEAM_MEMBER: 'team_member',
    PAYROLL: 'payroll',
    FINANCE: 'finance',
    SETTINGS: 'settings',
    /** Profile lives in the header user icon — not shown in the left nav. */
    PROFILE: 'profile',
    /** Notifications page — reached from the bell's "see all", not the left nav. */
    NOTIFICATIONS: 'notifications',
};

/**
 * Maps a notification category to the screen its click should open. Used by both
 * the header bell dropdown and the Notifications page so a click lands on the
 * relevant area (e.g. an appointment notification → Appointments). Returns null
 * when there's no sensible target.
 */
export const notificationTargetMenu = (category?: string): string | null => {
    if (!category) return null;
    if (category.startsWith('APPOINTMENT') || category === 'REVIEW_REQUEST' || category.startsWith('WAITLIST')) {
        return category.startsWith('WAITLIST') ? MENU.WAITLIST : MENU.APPOINTMENTS;
    }
    if (category.startsWith('PAYMENT')) return MENU.PAYMENTS;
    if (category === 'BIRTHDAY') return MENU.CLIENT;
    // EVENT_JOINED etc. → the Calendar opens the event's drawer (referenceId = eventId).
    if (category.startsWith('EVENT')) return MENU.CALENDAR;
    return null;
};

/**
 * Maps a left-nav screen to the permission key that gates it. Used to hide nav
 * items the caller can't access AND to redirect away from a screen they aren't
 * allowed to see (e.g. on F5). Screens without an entry are always allowed.
 */
export const MENU_PERMISSION: Record<string, string | string[]> = {
    // Home is the AI screen — gated by the business-controlled AI toggle.
    [MENU.HOME]: 'AI_ASSISTANT',
    [MENU.CALENDAR]: 'CALENDAR',
    [MENU.APPOINTMENTS]: 'CALENDAR',
    // Waitlist is a calendar sub-screen with its own permission. It's only shown
    // nested under Calendar (so CALENDAR visibility is the gate for the parent),
    // and additionally requires WAITLIST to view.
    [MENU.WAITLIST]: 'WAITLIST',
    [MENU.CLIENT]: 'CLIENTS',
    [MENU.PAYMENTS]: 'PAYMENTS',
    [MENU.REPORTS]: 'REPORTS',
    [MENU.CHATS]: 'CHAT',
    [MENU.SERVICES]: 'SERVICES',
    [MENU.TEAM_MEMBER]: 'TEAM',
    [MENU.PAYROLL]: 'PAYROLL',
    [MENU.FINANCE]: 'EXPENSES',
    // Settings shows with ANY settings access — even just the business calendar,
    // roles or payment, without the company-details permission.
    [MENU.SETTINGS]: ['BUSINESS_SETTINGS', 'WORKING_HOURS', 'ROLES_PERMISSIONS', 'PAYMENT_SETTINGS'],
};

/** Whether a left-nav screen is visible given the caller's canView checker.
 *  A screen mapped to several permissions is visible if ANY is viewable. */
export function menuAllowed(menuKey: string, canView: (permissionKey: string) => boolean): boolean {
    const p = MENU_PERMISSION[menuKey];
    if (!p) return true;
    return Array.isArray(p) ? p.some(canView) : canView(p);
}

/** Left-nav screens in display order — used to pick the first one a restricted
 *  user can land on. Profile is excluded (it's in the header). */
export const MENU_ORDER: string[] = [
    MENU.CALENDAR, MENU.APPOINTMENTS, MENU.CLIENT, MENU.PAYMENTS, MENU.REPORTS,
    MENU.CHATS, MENU.SERVICES, MENU.TEAM_MEMBER, MENU.PAYROLL,
    MENU.FINANCE, MENU.SETTINGS,
];

/**
 * Time dropdown options. The `value` stays canonical 12-hour ("1:00 PM") so the
 * stored/parsed/saved format never changes; only the `label` follows the business
 * time format (24h → "13:00"). Pass `is24h=true` to render 24-hour labels.
 */
export const generateTimeOptions = (is24h = false) => {
    const times = [];
    for (let hour = 0; hour < 24; hour++) {
        for (let minutes = 0; minutes < 60; minutes += 30) {
            const hour12 = hour % 12 === 0 ? 12 : hour % 12;
            const amPm = hour < 12 ? 'AM' : 'PM';
            const mm = minutes === 0 ? '00' : '30';
            const value = `${hour12}:${mm} ${amPm}`;
            const label = is24h ? `${hour < 10 ? '0' : ''}${hour}:${mm}` : value;
            times.push({ value, label });
        }
    }
    return times;
};